import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { renderGoogleButton, signInWithApple, ssoProviders } from '../../utils/sso';

const AppleLogo = () => (
    <svg viewBox="0 0 17 20" aria-hidden="true" className="h-5 w-5" fill="currentColor">
        <path d="M14.2 10.6c0-2.6 2.1-3.8 2.2-3.9-1.2-1.8-3.1-2-3.7-2-1.6-.2-3.1.9-3.9.9-.8 0-2-.9-3.4-.9-1.7 0-3.3 1-4.2 2.6-1.8 3.1-.5 7.7 1.3 10.2.9 1.2 1.9 2.6 3.2 2.6 1.3-.1 1.8-.8 3.3-.8 1.6 0 2 .8 3.4.8 1.4 0 2.3-1.3 3.1-2.5 1-1.4 1.4-2.8 1.4-2.9-.1 0-2.7-1-2.7-4.1zM11.7 3c.7-.9 1.2-2 1-3.2-1 .1-2.3.7-3 1.6-.7.8-1.2 2-1.1 3.1 1.2.1 2.3-.6 3.1-1.5z" />
    </svg>
);

/**
 * Google + Apple buttons. Calls onToken(provider, idToken).
 * mode: 'signin' (login page) or 'link' (settings).
 */
const SsoButtons = ({ onToken, onError, mode, disabled, providers }) => {
    const googleRef = useRef(null);
    const containerRef = useRef(null);
    const [appleBusy, setAppleBusy] = useState(false);
    const available = (providers || ssoProviders()).filter((p) => ssoProviders().includes(p));
    const showGoogle = available.includes('google');
    const showApple = available.includes('apple');

    // Keep the latest callback without re-rendering Google's button.
    const onTokenRef = useRef(onToken);
    onTokenRef.current = onToken;

    useEffect(() => {
        if (!showGoogle || !googleRef.current) return;
        const width = Math.min(containerRef.current?.offsetWidth || 320, 400);
        renderGoogleButton(googleRef.current, (token) => onTokenRef.current('google', token), {
            text: mode === 'link' ? 'continue_with' : 'signin_with',
            width,
        }).catch(() => onError && onError('Google sign-in could not be loaded. Check your connection and try again.'));
    }, [showGoogle, mode, onError]);

    const handleApple = async () => {
        setAppleBusy(true);
        try {
            const token = await signInWithApple();
            if (token) onTokenRef.current('apple', token);
        } catch (err) {
            if (onError) onError('Apple sign-in failed. Please try again.');
        } finally {
            setAppleBusy(false);
        }
    };

    if (!showGoogle && !showApple) return null;

    return (
        <div ref={containerRef} className={`flex flex-col gap-3 ${disabled ? 'pointer-events-none opacity-60' : ''}`} aria-busy={disabled}>
            {showGoogle && <div ref={googleRef} className="flex min-h-[44px] justify-center" />}
            {showApple && (
                <button
                    type="button"
                    onClick={handleApple}
                    disabled={disabled || appleBusy}
                    className="flex h-[44px] w-full items-center justify-center gap-2 rounded-md bg-black px-4 text-[15px] font-medium text-white transition-colors hover:bg-gray-800 disabled:opacity-60"
                >
                    <AppleLogo />
                    {mode === 'link' ? 'Continue with Apple' : 'Sign in with Apple'}
                </button>
            )}
        </div>
    );
};

SsoButtons.propTypes = {
    onToken: PropTypes.func.isRequired,
    onError: PropTypes.func,
    mode: PropTypes.oneOf(['signin', 'link']),
    disabled: PropTypes.bool,
    providers: PropTypes.arrayOf(PropTypes.string),
};

SsoButtons.defaultProps = {
    onError: undefined,
    mode: 'signin',
    disabled: false,
    providers: undefined,
};

export default SsoButtons;
